import { Link } from "@tanstack/react-router";
import { ROLE_LABEL, Shell } from "@/app/Shell";
import { TabBar } from "@/app/TabBar";
import { runnerAvailable, useAssignment, useWorkspace } from "@/app/notebookQueries";
import { useMe } from "@/app/session";
import { cellById, summarize } from "@/kernel/runner";
import { plural } from "@/lib/plural";
import { errorInfo } from "@/routes/step/model";
import { NotFound, SkeletonRows, StateCard } from "@/ui/states";
import { AutocheckGrid } from "./readonly/AutocheckGrid";
import { ReadOnlyCallout } from "./readonly/ReadOnlyCallout";
import { ServerCheck } from "./readonly/ServerCheck";
import { TaskBlock } from "./readonly/TaskBlock";

/** S8 — ноутбук только для чтения (< md вместо S5). С телефона домашку не
 *  решают (правило 9): ни редактора, ни запуска — код, последний вывод и
 *  статусы из черновика; ссылку на компьютер можно прислать в Telegram. */
export function ReadOnlyNotebook({ assignmentId }: { assignmentId: string }) {
  const me = useMe();
  const assignment = useAssignment(assignmentId);
  const workspace = useWorkspace(assignmentId);
  const user = me.data ? { name: me.data.name, role: ROLE_LABEL[me.data.role] ?? me.data.role } : undefined;

  const failed = assignment.status === "error" ? assignment.error : workspace.status === "error" ? workspace.error : null;
  if (failed) {
    const e = errorInfo(failed);
    return (
      <Shell nav user={user}>
        {e.code === "step_locked" || e.status === 403 ? (
          <StateCard code="LOCKED" where="домашка" title="Шаг ещё закрыт" body={e.message} primary={{ label: "вернуться к маршруту", href: "/" }} />
        ) : e.status === 404 ? (
          <NotFound />
        ) : (
          <StateCard
            code="ERR_NETWORK"
            where="загрузка ноутбука"
            tone="warn"
            title="Нет связи с сервером"
            body="Ноутбук не смог загрузиться. Черновик хранится на сервере и не пропал — откройте страницу снова, когда связь вернётся."
            primary={{
              label: "повторить сейчас",
              onClick: () => {
                if (assignment.status === "error") void assignment.refetch();
                if (workspace.status === "error") void workspace.refetch();
              },
            }}
          />
        )}
        <TabBar />
      </Shell>
    );
  }
  if (assignment.status !== "success" || workspace.status !== "success") {
    return (
      <Shell nav user={user}>
        <SkeletonRows rows={6} />
        <TabBar />
      </Shell>
    );
  }

  const a = assignment.data;
  const { notebook, results } = workspace.data;
  const tasks = a.manifest.tasks;
  const sum = summarize(tasks, results);

  return (
    <Shell nav user={user} wide>
      <div className="pb-[72px]">
        <div className="flex items-center gap-2.5 border-b border-bd px-4 pb-3 pt-2">
          <Link to="/" aria-label="к маршруту" className="font-mono text-[16px] text-ink2 no-underline">
            ←
          </Link>
          <div className="min-w-0 flex-1">
            <h1 className="m-0 truncate font-mono text-[12.5px] font-normal">{a.file_name}</h1>
            <div className="font-mono text-[10.5px] text-mut" data-testid="nb-summary">
              {sum.total} {plural(sum.total, "задача", "задачи", "задач")} · {sum.passed} прошло
            </div>
          </div>
        </div>

        <div className="px-4 pt-3.5">
          <ReadOnlyCallout assignmentId={assignmentId} />
        </div>

        <div className="flex flex-col gap-3 px-4 pb-5 pt-3.5">
          <AutocheckGrid tasks={tasks} results={results} passed={sum.passed} />
          {runnerAvailable(a.manifest.runtime) ? (
            <ServerCheck assignmentId={assignmentId} tasks={tasks} />
          ) : (
            <p className="m-0 rounded-md border border-warn bg-warnsoft px-3.5 py-2.5 text-[12.5px] leading-[1.5] text-ink" data-testid="offline-note">
              Автопроверки у этой домашки нет: среда в браузере не выполняется. С компьютера скачай ноутбук, реши локально или в Colab и отправь ментору.
            </p>
          )}
          {tasks.map((t) => (
            <TaskBlock key={t.n} task={t} code={cellById(notebook, t.cell_id)} checks={cellById(notebook, t.checks_cell_id)} result={results[String(t.n)]} />
          ))}
        </div>
      </div>
      <TabBar />
    </Shell>
  );
}
